import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * Custom hook for managing mention autocomplete state
 *
 * Handles @ trigger detection, member filtering, keyboard navigation,
 * and selection for implementing @mention autocomplete in message input.
 *
 * @param {Array<{userId: string, name?: string, rawDisplayName?: string}>} roomMembers - Room member list
 * @param {string|null} currentUserId - Current user's Matrix ID to exclude from suggestions (prevents self-mentions)
 * @returns {Object} Autocomplete state and handlers
 */
const useMentionAutocomplete = (roomMembers = [], currentUserId = null) => {
  // Autocomplete visibility
  const [isOpen, setIsOpen] = useState(false);

  // Current search text after @
  const [searchText, setSearchText] = useState('');

  // Currently highlighted suggestion index
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Trigger position in the input text
  const [triggerPosition, setTriggerPosition] = useState(null);

  /**
   * Maximum number of suggestions to show
   */
  const MAX_SUGGESTIONS = 7;

  /**
   * Filter room members based on search text
   * Matches against displayName and Matrix ID localpart (case-insensitive)
   * Excludes current user to prevent self-mentions
   * Only computes when autocomplete is open
   */
  const suggestions = useMemo(() => {
    if (!isOpen || !roomMembers?.length) return [];

    const search = searchText.toLowerCase();

    const filtered = roomMembers
      .filter(member => {
        // Exclude current user from suggestions (prevent self-mentions)
        if (currentUserId && member.userId === currentUserId) {
          return false;
        }

        const displayName = member.rawDisplayName || member.name || '';
        const userId = member.userId || '';

        // Extract localpart from Matrix ID (@alice:server.com -> alice)
        const localpart = userId.startsWith('@')
          ? userId.slice(1).split(':')[0]
          : '';

        // Case-insensitive match on displayName or localpart
        return (
          displayName.toLowerCase().includes(search) ||
          localpart.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        const nameA = (a.rawDisplayName || a.name || a.userId).toLowerCase();
        const nameB = (b.rawDisplayName || b.name || b.userId).toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .slice(0, MAX_SUGGESTIONS);

    return filtered;
  }, [isOpen, roomMembers, searchText, currentUserId]);

  /**
   * Reset selectedIndex when suggestions change
   */
  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions]);

  /**
   * Detect @ trigger and update autocomplete state
   *
   * @param {string} value - Current input value
   * @param {number} cursorPosition - Current cursor position
   */
  const handleInputChange = useCallback((value, cursorPosition) => {
    // Get text from start to cursor position
    const textBeforeCursor = value.slice(0, cursorPosition);

    // Detect @ trigger at word boundary
    // Match @ at start or after whitespace, followed by optional word chars, dots, or hyphens
    // Supports usernames like @terry.tucker or @john-doe
    const triggerMatch = textBeforeCursor.match(/(?:^|\s)@([\w.-]*)$/);

    if (triggerMatch) {
      const matchStart = textBeforeCursor.lastIndexOf('@');
      setIsOpen(true);
      setSearchText(triggerMatch[1] || '');
      setTriggerPosition({
        start: matchStart,
        end: cursorPosition,
      });
    } else {
      // No @ trigger found - close autocomplete
      if (isOpen) {
        setIsOpen(false);
        setSearchText('');
        setTriggerPosition(null);
      }
    }
  }, [isOpen]);

  /**
   * Generate formatted mention text for a member
   *
   * @param {Object} member - Room member object
   * @returns {string} Formatted mention text
   */
  const formatMentionText = useCallback((member) => {
    const displayName = member.rawDisplayName || member.name || member.userId;

    // Check if display name is ambiguous (multiple room members with same name)
    const normalizedName = displayName.toLowerCase();
    const hasDuplicate = roomMembers.some(m =>
      m.userId !== member.userId &&
      (m.rawDisplayName || m.name || '').toLowerCase() === normalizedName,
    );

    if (hasDuplicate) {
      // Include MXID suffix for disambiguation: @[Duplicate Name (@dupe1:server.com)]
      return `@[${displayName} (${member.userId})]`;
    }

    // Use bracket notation for names with spaces or special characters
    // Special chars include: space, colon, parentheses, brackets, etc.
    if (/[\s:()[\]<>]/.test(displayName)) {
      return `@[${displayName}]`;
    }

    return `@${displayName}`;
  }, [roomMembers]);

  /**
   * Handle mention selection
   *
   * @param {Object} member - Selected room member
   * @returns {Object} { mentionText, startIndex, endIndex } for text replacement
   */
  const selectMention = useCallback((member) => {
    if (!member || !triggerPosition) return null;

    const mentionText = formatMentionText(member);

    const result = {
      mentionText,
      startIndex: triggerPosition.start,
      endIndex: triggerPosition.end,
    };

    // Close autocomplete after selection
    setIsOpen(false);
    setSearchText('');
    setSelectedIndex(0);
    setTriggerPosition(null);

    return result;
  }, [triggerPosition, formatMentionText]);

  /**
   * Handle keyboard navigation and selection
   *
   * @param {KeyboardEvent} event - Keyboard event
   * @returns {boolean} True if event was consumed
   */
  const handleKeyDown = useCallback((event) => {
    if (!isOpen || !suggestions.length) return false;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev >= suggestions.length - 1 ? 0 : prev + 1,
        );
        return true;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev <= 0 ? suggestions.length - 1 : prev - 1,
        );
        return true;

      case 'Enter':
        if (suggestions[selectedIndex]) {
          event.preventDefault();
          // Return the selected mention for the caller to handle
          return selectMention(suggestions[selectedIndex]);
        }
        return false;

      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setSearchText('');
        setSelectedIndex(0);
        setTriggerPosition(null);
        return true;

      case 'Tab':
        // Tab can also select current suggestion
        if (suggestions[selectedIndex]) {
          event.preventDefault();
          return selectMention(suggestions[selectedIndex]);
        }
        return false;

      default:
        return false;
    }
  }, [isOpen, suggestions, selectedIndex, selectMention]);

  /**
   * Close the autocomplete and reset state
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setSearchText('');
    setSelectedIndex(0);
    setTriggerPosition(null);
  }, []);

  return {
    isOpen,
    suggestions,
    selectedIndex,
    searchText,
    triggerPosition,
    handleInputChange,
    handleKeyDown,
    selectMention,
    close,
  };
};

export default useMentionAutocomplete;
